/**
 * Sheet 04, the rule catalog (mission 6 WP10 T052; FR-020, FR-023, NFR-005,
 * NFR-007; US9; ANA.3, ANA.4, ANA.6, ANA.7, ANA.9, T.2; C-009, C-013;
 * DM-01M3BGSYXX5C2KHX81NNMGD5G6; research §1.21; post-tasks P6, R7, E2).
 *
 * Read-only over one GET /v1/rules (`['rules']`, the stated exception to the
 * NFR-005 list bound). The header carries the rule-set version and the count
 * from the read, never a literal; the ANA.7 statement exactly as the API gives
 * it; per-severity chips computed from the same read; the ANA.4 legend and the
 * T.2 band, which render in every state and are never dismissible (CLAUDE.md
 * §3.5).
 *
 * Filters (severity, segment, text) are component state and narrow the rows
 * client-side. Only `?rule=` is a URL key: it names the one expanded row, is
 * scrolled to once when it arrives as a deep link (the drawer's "View in rule
 * catalog"), and is replaced, never pushed, when a toggle changes it. An id
 * that is malformed or not in the read renders register 16's unknown-rule
 * line and is never requested.
 *
 * Absent, not disabled (C-009, C-013): the version selector (04/1) and the
 * Implemented / Not implemented switch (04/6, 04/12). The expanded row's
 * recent-run and assistant links (04/11) are `CatalogTable`'s, read from the
 * rule's latest run (mission 8 WP09 T049; OR-8). The catalog always lands
 * (R7): the T.2 band and the finding drawer link here.
 */
import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'

import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Band } from '@/components/trellis/Band'
import { ProblemBand } from '@/components/trellis/ProblemBand'
import { SeverityCountChip } from '@/components/trellis/SeverityCountChip'
import { SeverityLegend } from '@/components/trellis/SeverityLegend'
import { T2Band } from '@/components/trellis/T2Band'
import { ApiError, InvalidIdError, validRuleId } from '@/lib/api'
import type { RuleSummary } from '@/lib/api/rules'
import { Mono, count } from '@/lib/format'
import { copyText } from '@/lib/provisional-copy'
import { rulesQuery } from '@/lib/queries'
import { SEVERITY_ORDER, type Severity } from '@/lib/severity'

import { CatalogTable } from './CatalogTable'

/** The segment Select's "all" value; Radix refuses an empty item value. */
const ALL_SEGMENTS = '*'

const EMPTY: RuleSummary[] = []

function wellFormed(id: string): boolean {
  try {
    validRuleId(id)
    return true
  } catch (err) {
    if (err instanceof InvalidIdError) return false
    throw err
  }
}

function matches(rule: RuleSummary, needle: string): boolean {
  return [rule.id, rule.field ?? '', rule.description].some((value) => value.toLowerCase().includes(needle))
}

export default function Catalog() {
  const query = useQuery(rulesQuery())
  const [params, setParams] = useSearchParams()
  const [severity, setSeverity] = useState<Severity | null>(null)
  const [segment, setSegment] = useState<string | null>(null)
  const [q, setQ] = useState('')

  const rules = query.data?.rules ?? EMPTY
  const ruleParam = params.get('rule')
  const expanded = ruleParam !== null && rules.some((r) => r.id === ruleParam) ? ruleParam : null
  const unknownRule =
    ruleParam !== null && (!wellFormed(ruleParam) || (query.isSuccess && expanded === null))

  const segments = useMemo(() => Array.from(new Set(rules.map((r) => r.segment))), [rules])
  const severityCounts = useMemo(() => {
    const counts: Record<Severity, number> = { ERROR: 0, WARN: 0, INFO: 0 }
    for (const rule of rules) counts[rule.default_severity] += 1
    return counts
  }, [rules])
  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return rules.filter(
      (rule) =>
        (severity === null || rule.default_severity === severity) &&
        (segment === null || rule.segment === segment) &&
        (needle === '' || matches(rule, needle)),
    )
  }, [rules, severity, segment, q])

  // The deep-link scroll: once, after the rows exist (`expanded` stays null
  // until the read lands), and never for a toggle the user clicked. Focus is
  // not moved.
  const row = useRef<HTMLTableRowElement | null>(null)
  const fromToggle = useRef(false)
  useLayoutEffect(() => {
    if (expanded !== null && !fromToggle.current) row.current?.scrollIntoView({ block: 'center' })
    fromToggle.current = false
  }, [expanded])

  function toggle(id: string) {
    fromToggle.current = true
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (expanded === id) next.delete('rule')
        else next.set('rule', id)
        return next
      },
      { replace: true },
    )
  }

  const data = query.data

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-3">
        <h1 className="text-h1 text-navy-950">Rule catalog</h1>
        {data ? (
          <>
            <p className="text-body-sm text-navy-600">
              {copyText('ruleSetVersionLabel')} <Mono>{data.rule_set_version}</Mono> · <Mono>{count(data.count)}</Mono>{' '}
              rules
            </p>
            <p className="max-w-[80ch] text-body text-navy-950">{data.statement}</p>
            <div className="flex flex-wrap gap-2">
              {SEVERITY_ORDER.map((s) => (
                <SeverityCountChip
                  key={s}
                  severity={s}
                  count={severityCounts[s]}
                  selected={severity === s}
                  onSelect={() => setSeverity((current) => (current === s ? null : s))}
                />
              ))}
            </div>
          </>
        ) : query.isPending ? (
          <div aria-busy="true" className="flex flex-col gap-2">
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-4 w-full max-w-[80ch]" />
          </div>
        ) : null}
        <SeverityLegend />
        <T2Band />
      </header>

      {query.isError ? (
        query.error instanceof ApiError ? (
          <ProblemBand problem={query.error.problem} />
        ) : (
          <Band kind="blocked">{copyText('genericError')}</Band>
        )
      ) : (
        <>
          {data ? (
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex w-56 flex-col gap-1.5">
                <Label htmlFor="catalog-segment">Segment</Label>
                <Select
                  value={segment ?? ALL_SEGMENTS}
                  onValueChange={(value) => setSegment(value === ALL_SEGMENTS ? null : value)}
                >
                  <SelectTrigger id="catalog-segment">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_SEGMENTS}>{copyText('allSegments')}</SelectItem>
                    {segments.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex w-80 flex-col gap-1.5">
                <Label htmlFor="catalog-search">Search rules</Label>
                <Input id="catalog-search" type="search" value={q} onChange={(e) => setQ(e.target.value)} />
              </div>
            </div>
          ) : null}
          {unknownRule ? (
            <p role="status" className="text-body text-navy-950">
              {copyText('unknownRule')}
            </p>
          ) : null}
          <CatalogTable
            rows={visible}
            loading={query.isPending}
            expanded={expanded}
            onToggle={toggle}
            rowRef={(el) => {
              row.current = el
            }}
            total={rules.length}
          />
        </>
      )}
    </div>
  )
}
